import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Container, Form, Modal, Spinner } from 'react-bootstrap'
import AbstractPdfViewer from './AbstractPdfViewer'

import { pdfjs } from 'react-pdf'
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface AreaOfInterest {
  key: string
  name: string
  count: number
}

interface Attachment {
  id: number
  url: string
  filename: string
}

interface Coauthor {
  id: number
  name?: string
  institution?: string
}

interface AbstractItem {
  id: number
  poster_id?: string
  title: string
  area_of_interest: string
  first_author_name: string
  presenting_author_name: string
  format: string // Allow string for flexibility if API returns other values
  featured_poster?: boolean
  poster?: Attachment
  coauthors?: Coauthor[]
}

interface FetchedFormat {
  key: string
  name: string
}

const GalleryWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="abstract-gallery-wrapper">
    {children}
    <style type="text/css">
      {`
        .abstract-gallery-wrapper {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
          padding: 2rem 0;
        }

        .header-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.875rem;
        }

        h1 {
          margin-bottom: 1rem;

          color: #292929;
          font-family: Montserrat;
          font-size: 2.5rem;
          font-style: normal;
          font-weight: 500;
          line-height: normal;

          @media (max-width: 768px) {
            font-size: 2rem;
          }
        }
        h2 {
          color: #292929;
          font-family: Montserrat;
          font-size: 1.25rem;
          font-style: normal;
          font-weight: 700;
          line-height: normal;

          @media (max-width: 768px) {
            color: #292929;
            font-family: Montserrat;
            font-size: 1rem;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
          }
        }
        h3 {
          margin: 0;
          color: #fff;
          font-family: Montserrat;
          font-size: 1.0625rem;
          font-style: normal;
          font-weight: 700;
          line-height: 1.5rem;

          @media (max-width: 768px) {
            color: #fff;
            font-family: Montserrat;
            font-size: 0.875rem;
            font-style: normal;
            font-weight: 700;
            line-height: 1.5rem;
          }
        }
        h4 {
          margin: 0;
          color: #212529;
          font-family: Montserrat;
          font-size: 0.875rem;
          font-style: normal;
          font-weight: 600;
          line-height: 1.25rem;

          @media (max-width: 768px) {
            color: #212529;
            font-family: Montserrat;
            font-size: 0.875rem;
            font-style: normal;
            font-weight: 600;
            line-height: 1.25rem;
          }
        }
        h5 {
          color: #212529;
          font-family: Montserrat;
          font-size: 0.875rem;
          font-style: normal;
          font-weight: 400;
          line-height: 1.25rem;

          @media (max-width: 768px) {
            color: #212529;
            font-family: Montserrat;
            font-size: 0.875rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.25rem;
          }
        }

        .abstract-formats {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 45px;

          @media (max-width: 768px) {
            gap: 0;
          }
        }
        .format-btns {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
        }
        .format-btn {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          padding: 9px 24px;
          border-radius: 50px;
          border: 1px solid #d1d5db;
          background: #fff;
          box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
          color: #3a3231;
          font-family: Inter;
          font-size: 0.875rem;
          font-style: normal;
          font-weight: 600;
          line-height: 1.5rem;
          cursor: pointer;

          &.active {
            background: #9bd4ff;
            border: 1px solid #9bd4ff;
          }
        }
        .featured-btn {
          &.active {
            background: #ffe200;
            border: 1px solid #ffe200;
          }
        }

        .abstract-cards-container {
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          padding: 34px 0;
          background: #ebebeb;
          width: 100%;

          @media (max-width: 768px) {
            padding: 16px 0;
          }
        }
        .abstract-area-of-interests {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 12px;

          @media (max-width: 768px) {
            gap: 4px;
          }
        }
        .area-of-interest {
          display: flex;
          padding: 0px 4px 16px 4px;
          justify-content: center;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          border-bottom: 2px solid transparent;

          color: var(--gray-500, #6b7280);
          font-family: Montserrat;
          font-size: 0.875rem;
          font-style: normal;
          font-weight: 500;
          line-height: 1.25rem; /* 142.857% */

          @media (max-width: 768px) {
            padding: 0px 4px 4px 4px;

            color: var(--gray-500, #6b7280);
            font-family: Montserrat;
            font-size: 0.75rem;
            font-style: normal;
            font-weight: 500;
            line-height: 1.25rem;
          }

          &.active {
            color: #133a57;
            border-bottom: 2px solid #133a57;

            .area-of-interest-count {
              background: #133a57;
              color: #fff;
            }
          }
        }
        .area-of-interest-count {
          display: flex;
          padding: 2px 10px;
          justify-content: center;
          align-items: center;
          border-radius: 10px;
          background: #f0f0f0;

          color: var(--gray-800, #1f2937);
          text-align: center;
          font-family: Montserrat;
          font-size: 0.75rem;
          font-style: normal;
          font-weight: 500;
          line-height: 1rem;
        }

        .abstract-cards {
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e0e0e0;
        }
        .abstract-cards-title {
          padding: 20px 24px;
          background: #133a57;
        }
        .abstract-card {
          display: flex;
          padding: 16px;
          flex-direction: column;
          gap: 8px;
          border-bottom: 1px solid #e0e0e0;
          background-color: #fff;
          cursor: pointer;
          &:hover {
            background-color: #f5f8ff;
          }
        }
        .abstract-title-id {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 8px;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        }
        .abstract-id {
          min-width: 35px;
          padding: 4px 8px;

          border-radius: 6px;
          background: #f3f7ff;

          color: var(--gray-700, #374151);
          font-family: Montserrat;
          font-size: 0.75rem;
          font-style: normal;
          font-weight: 500;
          line-height: 1.25rem;
        }
        .abstract-authors {
          display: flex;
          align-items: center;
          gap: 6px;

          span {
            color: #212529;
            font-family: Montserrat;
            font-size: 0.875rem;
            font-style: normal;
            font-weight: 400;
            line-height: 1.25rem;
          }
        }

        .abstract-pdf {
          padding: 0;

          iframe {
            width: 100%;
            height: 100dvh;
          }
        }
      `}
    </style>
  </div>
)

const svgPaper = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M13.125 14.375V17.1875C13.125 17.7053 12.7053 18.125 12.1875 18.125H4.0625C3.54473 18.125 3.125 17.7053 3.125 17.1875V6.5625C3.125 6.04473 3.54473 5.625 4.0625 5.625H5.625C6.05089 5.625 6.46849 5.6605 6.875 5.7287M13.125 14.375H15.9375C16.4553 14.375 16.875 13.9553 16.875 13.4375V9.375C16.875 5.65876 14.1721 2.5738 10.625 1.9787C10.2185 1.9105 9.80089 1.875 9.375 1.875H7.8125C7.29473 1.875 6.875 2.29473 6.875 2.8125V5.7287M13.125 14.375H7.8125C7.29473 14.375 6.875 13.9553 6.875 13.4375V5.7287M16.875 11.25V9.6875C16.875 8.1342 15.6158 6.875 14.0625 6.875H12.8125C12.2947 6.875 11.875 6.45527 11.875 5.9375V4.6875C11.875 3.1342 10.6158 1.875 9.0625 1.875H8.125"
      stroke="#3A3231"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const svgUser = (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M9 6C9 7.65685 7.65685 9 6 9C4.34315 9 3 7.65685 3 6C3 4.34315 4.34315 3 6 3C7.65685 3 9 4.34315 9 6Z"
      fill="#9CA3AF"
    />
    <path
      d="M17 6C17 7.65685 15.6569 9 14 9C12.3431 9 11 7.65685 11 6C11 4.34315 12.3431 3 14 3C15.6569 3 17 4.34315 17 6Z"
      fill="#9CA3AF"
    />
    <path
      d="M12.9291 17C12.9758 16.6734 13 16.3395 13 16C13 14.3648 12.4393 12.8606 11.4998 11.6691C12.2352 11.2435 13.0892 11 14 11C16.7614 11 19 13.2386 19 16V17H12.9291Z"
      fill="#9CA3AF"
    />
    <path
      d="M6 11C8.76142 11 11 13.2386 11 16V17H1V16C1 13.2386 3.23858 11 6 11Z"
      fill="#9CA3AF"
    />
  </svg>
)

interface SvgProps {
  color?: string
}

const SvgStar: React.FC<SvgProps> = ({ color }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2.5 3.00098H21.5"
      stroke={color || '#9CA3AF'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 3.00098V14C4.5 16.3288 4.93059 17.0893 6.92752 18.2875L9.94202 20.0962C10.9447 20.6978 11.446 20.9986 12 20.9986C12.554 20.9986 13.0553 20.6978 14.058 20.0962L17.0725 18.2875C19.0694 17.0893 19.5 16.3288 19.5 14V3.00098"
      stroke={color || '#9CA3AF'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12.6045 8.00546L13.2204 9.24751C13.3044 9.4204 13.5284 9.58625 13.7173 9.618L14.8337 9.80501C15.5476 9.92498 15.7156 10.4472 15.2012 10.9624L14.3333 11.8374C14.1863 11.9856 14.1058 12.2715 14.1513 12.4761L14.3998 13.5594C14.5957 14.4168 14.1443 14.7485 13.3919 14.3004L12.3455 13.6758C12.1565 13.5629 11.845 13.5629 11.6526 13.6758L10.6062 14.3004C9.85726 14.7485 9.40231 14.4133 9.59829 13.5594L9.84676 12.4761C9.89226 12.2715 9.81177 11.9856 9.66478 11.8374L8.79688 10.9624C8.28594 10.4472 8.45042 9.92498 9.16434 9.80501L10.2807 9.618C10.4662 9.58625 10.6902 9.4204 10.7742 9.24751L11.3901 8.00546C11.7261 7.33151 12.272 7.33151 12.6045 8.00546Z"
      stroke={color || '#9CA3AF'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AbstractGallery = () => {
  const [availableFormats, setAvailableFormats] = useState<FetchedFormat[]>([])
  const [isLoadingFormats, setIsLoadingFormats] = useState<boolean>(true)
  const [selectedFormat, setSelectedFormat] = useState<string | null>(null)
  const [selectedAreaOfInterest, setSelectedAreaOfInterest] = useState<
    string | null
  >(null)
  const [activeAbstract, setActiveAbstract] = useState<AbstractItem | null>(
    null
  )
  const [showPdfModal, setShowPdfModal] = useState<boolean>(false)

  const [abstractsData, setAbstractsData] = useState<AbstractItem[]>([])
  const [areasOfInterest, setAreasOfInterest] = useState<AreaOfInterest[]>([])
  const [isLoadingAbstracts, setIsLoadingAbstracts] = useState<boolean>(true)
  const [isLoadingAreas, setIsLoadingAreas] = useState<boolean>(true)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showFeaturedOnly, setShowFeaturedOnly] = useState<boolean>(false)

  useEffect(() => {
    setIsLoadingFormats(true)
    fetch('/api/abstract_gallery/available_formats')
      .then((res) => res.json())
      .then((data: FetchedFormat[]) => {
        setAvailableFormats(data)
        if (data.length > 0) {
          setSelectedFormat(data[0].key) // Select the first format by default
        }
        setIsLoadingFormats(false)
      })
      .catch((error) => {
        console.error('Error fetching available formats:', error)
        setAvailableFormats([])
        setIsLoadingFormats(false)
      })
  }, [])

  useEffect(() => {
    // Fetch and set areas of interest based on selectedFormat.
    if (!selectedFormat) {
      if (!isLoadingFormats && availableFormats.length === 0) {
        setAreasOfInterest([])
        setSelectedAreaOfInterest(null)
        setIsLoadingAreas(false)
      }
      return
    }

    // Wait for formats to finish loading.
    if (isLoadingFormats) {
      return
    }

    // Fetch areas for the selectedFormat
    setIsLoadingAreas(true)
    fetch(`/api/abstract_gallery/areas_of_interest?format=${selectedFormat}`)
      .then((res) => res.json())
      .then((data: AreaOfInterest[]) => {
        setAreasOfInterest(data)
        if (data.length > 0) {
          const currentAreaStillInNewData = data.some(
            (area) => area.key === selectedAreaOfInterest
          )
          if (!(selectedAreaOfInterest && currentAreaStillInNewData)) {
            setSelectedAreaOfInterest(data[0].key)
          }
        } else {
          setSelectedAreaOfInterest(null)
        }
        setIsLoadingAreas(false)
      })
      .catch((error) => {
        console.error(
          `[AoI useEffect] Error fetching areas of interest for ${selectedFormat}:`,
          error
        )
        // Preserve stale data on error to prevent "fetch all" side-effect for abstracts
        // if areas were previously loaded successfully.
        // If areasOfInterest was already empty, this doesn't change it.
        // If it had data, it keeps it, which is better than clearing and fetching all abstracts.
        setIsLoadingAreas(false)
        // Optionally, set a dedicated error state here to inform the user.
        // setAreasFetchError(true);
      })
  }, [selectedFormat, isLoadingFormats]) // availableFormats removed as direct dependency for fetching,
  // its loading state (isLoadingFormats) and initial check are used.

  useEffect(() => {
    // Wait for formats to finish loading.
    if (isLoadingFormats) {
      return
    }

    // No format selected yet.
    if (!selectedFormat) {
      setAbstractsData([])
      setIsLoadingAbstracts(false)
      return
    }

    // Wait for areas to finish loading.
    if (isLoadingAreas) {
      return
    }

    // Formats and areas are loaded, and selectedAreaOfInterest is set.

    let apiUrl = `/api/abstract_gallery/abstracts?format=${encodeURIComponent(
      selectedFormat ?? ''
    )}`
    let shouldFetch = true // Assume we fetch unless specific conditions prevent it.

    // If searching or showing featured only, fetch all abstracts for the format and filter client-side.
    if (searchTerm || showFeaturedOnly) {
      if (selectedAreaOfInterest !== null) {
        setSelectedAreaOfInterest(null)
        shouldFetch = false
      }
    } else {
      if (areasOfInterest.length > 0) {
        if (selectedAreaOfInterest) {
          apiUrl += `&area_of_interest=${encodeURIComponent(
            selectedAreaOfInterest
          )}`
        } else {
          setSelectedAreaOfInterest(areasOfInterest[0].key)
          shouldFetch = false
        }
      }
    }

    if (shouldFetch) {
      setIsLoadingAbstracts(true)
      fetch(apiUrl)
        .then((res) => res.json())
        .then((data) => {
          setAbstractsData(data)
          setIsLoadingAbstracts(false)
        })
        .catch((error) => {
          console.error(
            '[Abstracts useEffect] Error fetching abstracts:',
            error
          )
          setAbstractsData([]) // Clear data on error
          setIsLoadingAbstracts(false)
        })
    }
  }, [
    selectedFormat,
    selectedAreaOfInterest,
    isLoadingFormats,
    isLoadingAreas,
    areasOfInterest,
    searchTerm, // Add searchTerm as a dependency
    showFeaturedOnly, // Add showFeaturedOnly as a dependency
  ])

  const handleFormatSelect = (format: string) => {
    setSelectedFormat(format)
    // When format changes, always reset AoI-related states.
    // The AoI useEffect will then fetch/determine the correct areas and selected AoI for the new format.
    setAreasOfInterest([])
    setSelectedAreaOfInterest(null)
    setIsLoadingAreas(true) // Proactively set this.
    // The AoI useEffect will also set it (redundantly but harmlessly) and then set it to false when done.
    // This ensures the Abstracts useEffect sees isLoadingAreas as true immediately after a format change.
  }

  const handleAreaSelect = (area: string | null) => {
    // When an area is selected, clear search term and featured only
    setSearchTerm('')
    setShowFeaturedOnly(false)
    setSelectedAreaOfInterest(area)
  }

  const handleCardClick = (abstractItem: AbstractItem) => {
    if (abstractItem.poster && abstractItem.poster.url) {
      setActiveAbstract(abstractItem)
      setShowPdfModal(true)
    } else {
      console.warn(
        'No poster URL available for this abstract:',
        abstractItem.title
      )
      alert('No PDF available for this abstract.')
    }
  }

  const handleCloseModal = () => {
    setShowPdfModal(false)
    setActiveAbstract(null)
  }

  const getAuthorDisplay = (item: AbstractItem) => {
    const authors = [
      item.first_author_name,
      item.presenting_author_name,
    ].filter(Boolean)
    // Remove duplicates if first and presenting author are the same and convert Set to Array
    return Array.from(new Set(authors)).join(', ')
  }

  const filteredAbstracts = useMemo(() => {
    let abstractsToFilter = abstractsData
    if (!abstractsToFilter) return []

    // Apply featured filter first
    if (showFeaturedOnly) {
      abstractsToFilter = abstractsToFilter.filter(
        (abs) => abs.featured_poster === true
      )
    }

    // Then apply search term filter
    if (!searchTerm) {
      return abstractsToFilter
    }

    return abstractsToFilter.filter(
      (abs) =>
        abs.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        getAuthorDisplay(abs)
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        abs.id.toString().includes(searchTerm) ||
        (abs.poster_id &&
          abs.poster_id.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }, [abstractsData, searchTerm, showFeaturedOnly])

  if (isLoadingFormats || isLoadingAreas) {
    // Check isLoadingFormats as well
    return (
      <GalleryWrapper>
        <Container>
          <div className="header-container">
            <h1>Posters</h1>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </Container>
      </GalleryWrapper>
    )
  }

  return (
    <GalleryWrapper>
      <Container>
        <h1>Posters</h1>

        <div className="header-container">
          <div className="abstract-formats">
            <h4>Select poster format:</h4>
            <div className="format-btns">
              {availableFormats.map((format) => (
                <div
                  key={format.key}
                  className={`format-btn ${
                    selectedFormat === format.key ? 'active' : ''
                  }`}
                  onClick={() => {
                    handleFormatSelect(format.key)
                  }}
                >
                  {svgPaper}
                  <span>{format.name}</span>
                </div>
              ))}
              <div
                className={` featured-btn format-btn ${
                  showFeaturedOnly ? 'active' : ''
                }`}
                onClick={() => {
                  setShowFeaturedOnly(!showFeaturedOnly)
                }}
              >
                <SvgStar color="#3a3231" />
                <span>Featured Poster</span>
              </div>
            </div>
          </div>

          <Form.Control
            type="text"
            placeholder="Search by topic, author, or ID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', maxWidth: '300px' }}
          />
        </div>
      </Container>

      <hr style={{ height: '1px', backgroundColor: '#DDD' }} />

      {/* Render Areas of Interest section if areasOfInterest array is not empty and not loading,
          AND the selectedFormat actually has areas (which is implied by areasOfInterest.length > 0 
          after the AoI useEffect runs)
          AND no search term is active AND featured only is not active
      */}
      {!isLoadingAreas &&
        areasOfInterest.length > 0 &&
        !searchTerm &&
        !showFeaturedOnly && (
          <Container>
            <div className="abstract-area-of-interests">
              {isLoadingAreas && areasOfInterest.length === 0 ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    minHeight: '50px', // Ensure spinner is visible
                  }}
                >
                  <Spinner animation="border" size="sm" role="status">
                    <span className="visually-hidden">Loading areas...</span>
                  </Spinner>
                </div>
              ) : areasOfInterest.length > 0 ? (
                areasOfInterest.map((area) => (
                  <div
                    key={area.key}
                    className={`area-of-interest ${
                      selectedAreaOfInterest === area.key ? 'active' : ''
                    }`}
                    onClick={() => handleAreaSelect(area.key)}
                  >
                    <span>{area.name}</span>
                    <div className="area-of-interest-count">{area.count}</div>
                  </div>
                ))
              ) : (
                !isLoadingAreas && (
                  <p
                    style={{
                      textAlign: 'center',
                      color: '#6c757d',
                      width: '100%',
                    }}
                  >
                    No areas of interest available for this format.
                  </p>
                )
              )}
            </div>
          </Container>
        )}
      <div className="abstract-cards-container">
        <Container className="d-flex flex-column flex-grow-1">
          {isLoadingAbstracts ? (
            <div className="d-flex justify-content-center align-items-center flex-grow-1">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : filteredAbstracts.length > 0 ? (
            <div className="abstract-cards">
              <div className="abstract-cards-title">
                <h3>Posters</h3>
              </div>
              {filteredAbstracts.map((abs) => (
                <div
                  key={abs.id}
                  className="abstract-card"
                  onClick={() => handleCardClick(abs)}
                >
                  <div className="abstract-title-id">
                    <div className="d-flex gap-1">
                      {abs.featured_poster && <SvgStar />}
                      <h4 className="abstract-title">{abs.title}</h4>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <div className="abstract-id">{abs.id}</div>
                      {abs.poster_id && (
                        <div className="abstract-id">{abs.poster_id}</div>
                      )}
                    </div>
                  </div>
                  <div className="abstract-authors">
                    {svgUser}
                    <span>{getAuthorDisplay(abs)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p
              style={{
                textAlign: 'center',
                color: '#6c757d',
                marginTop: '2rem',
              }}
            >
              No abstracts match the current filter criteria or no abstracts
              available.
            </p>
          )}

          {activeAbstract && activeAbstract.poster && (
            <Modal
              show={showPdfModal}
              onHide={handleCloseModal}
              size="xl"
              centered
            >
              <Modal.Header
                closeButton
                style={{
                  padding: '20px 24px',
                }}
              >
                <Modal.Title>Preview</Modal.Title>
              </Modal.Header>
              <Modal.Body className="abstract-pdf">
                <AbstractPdfViewer file={activeAbstract.poster.url} />
              </Modal.Body>
            </Modal>
          )}
        </Container>
      </div>
    </GalleryWrapper>
  )
}

export default AbstractGallery
